import React, { useState } from "react";
import { useDraft } from "@/lib/drafts";
import { goToCheckout } from "@/lib/checkout";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useB2C } from "@/context/B2CAuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { HandHeart, PackageCheck } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { PhoneInput } from "@/components/PhoneInput";
import { eur } from "@/components/PublicationPlanPicker";
import { IdentityFields, ItemDetailFields, accountIdentity, MissingPhoneField, savePhoneIfMissing, PublishedAs, EXTRA_DEFAULTS, usePrefillFromProfile } from "@/components/DeclarationFields";
import { FindoSpinner } from "@/components/FindoSpinner";

const field = "mt-1.5 h-11 rounded-xl";
const INIT = { ...EXTRA_DEFAULTS, owner_email: "", owner_phone: "", country: "FR", found_date: "", found_place: "", photo: "", photo_2: "", message: "", consent: false };

export default function FinderDeclarationDialog({ ad, user, onClose, onSent }) {
  const { refreshUser } = useB2C();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState(INIT);
  const [busy, setBusy] = useState(false);
  usePrefillFromProfile(user, setForm);
  const claim = ad?.ad_type === "found";
  const needsPhoto = !claim;
  const draft = useDraft(ad ? `${claim ? "claim" : "finder_decl"}:${ad.id}` : "", form, setForm, { enabled: !!(ad && user), label: ad?.description });
  const photosOk = !(needsPhoto && !form.photo);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    if (!form.consent) { toast.error(t("an.shareConsentError")); return; }
    if (!photosOk) { toast.error(claim ? t("fa.twoPhotosError") : t("fr.photoError")); return; }
    setBusy(true);
    try {
      const idf = accountIdentity(user, form);
      await savePhoneIfMissing(user, idf.owner_phone, refreshUser);
      const first = idf.owner_first_name.trim(); const last = idf.owner_last_name.trim();
      const res = await api(`/public-ads/${ad.id}/contact`, {
        method: "POST", auth: false,
        body: {
          finder_name: `${first} ${last}`, finder_first_name: first, finder_last_name: last,
          finder_email: idf.owner_email.trim().toLowerCase(), finder_phone: idf.owner_phone,
          found_date: form.found_date, found_place: form.found_place, city: form.city.trim(),
          circumstances: form.circumstances.trim(), color: form.color.trim(), condition: form.condition.trim(),
          brand: form.brand.trim() || null, quantity: Number(form.quantity) || 1, photo: form.photo || null, photo_2: form.photo_2 || null,
          message: claim ? form.message : "", share_consent: true, lang, contribution_eur: 0,
          origin_url: window.location.origin, return_path: `/suivi-trouve/${ad.id}`,
        },
      });
      onSent?.();
      if (res?.url) { draft.clear(); toast.message(t("fa.redirectStripe")); goToCheckout(res.url); return; }
      draft.clear();
      toast.success(t(claim ? "fa.claimSentFinder" : "fa.sent"));
      navigate(`/suivi-trouve/${ad.id}`);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  }

  return (
    <Dialog open={!!ad} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="finder-decl-dialog">
        <DialogHeader>
          <DialogTitle icon={claim ? HandHeart : PackageCheck} className="font-display">{t(claim ? "fa.claimTitle" : "fa.declTitle")}</DialogTitle>
          <DialogDescription>{t(claim ? "fa.claimSubV2" : "fa.declSub")}</DialogDescription>
        </DialogHeader>
        {ad && (
          <form onSubmit={submit} className="findo-numbered space-y-5" data-testid="finder-decl-form">
            {claim && <p className="rounded-xl bg-amber-50 border border-amber-200 px-3.5 py-2.5 text-xs font-semibold text-amber-800" data-testid="fd-check-info">{t("fa.checkInfo")}</p>}
            {!user && (<>
              <IdentityFields form={form} setForm={setForm} prefix="fd" />
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("mc.email")} <span className="text-findo-red">*</span></Label><Input type="email" required value={form.owner_email} onChange={set("owner_email")} className={field} data-testid="fd-email" /></div>
                <div><Label>{t("mc.phone")} <span className="text-findo-red">*</span></Label><PhoneInput country={form.country} value={form.owner_phone} onChange={(v) => setForm({ ...form, owner_phone: v })} required testid="fd-phone" /></div>
              </div>
            </>)}
            <PublishedAs user={user} testid="fd-published-as" />
            <MissingPhoneField user={user} form={form} setForm={setForm} prefix="fd" />
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t(claim ? "fa.lostDate" : "fa.foundDate")} <span className="text-findo-red">*</span></Label><Input type="date" required value={form.found_date} onChange={set("found_date")} className={field} data-testid="fd-found-date" /></div>
              <div><Label>{t(claim ? "fa.lostPlace" : "fa.foundPlace")} <span className="text-findo-red">*</span></Label><div className="mt-1.5"><CityAutocomplete value={form.found_place} onChange={(v) => setForm({ ...form, found_place: v })} country={ad.country} className="h-11 rounded-xl" testid="fd-found-place" /></div></div>
            </div>
            <ItemDetailFields form={form} setForm={setForm} prefix="fd" found={!claim} category={ad.category} />
            <div data-testid="fd-photo-field">
              <Label>{claim ? t("fa.photosOptional") : t("fr.photoLabel")} {needsPhoto && <span className="text-findo-red">*</span>}</Label>
              <p className="text-xs text-slate-500 mt-0.5 mb-1.5">{claim ? t("fa.twoPhotosHint") : t("fr.photoHint")}</p>
              <div className={claim ? "grid grid-cols-2 gap-3" : ""}>
                <ImageUpload secure="finder" value={form.photo} onChange={(v) => setForm((f) => ({ ...f, photo: v }))} testid="fd-photo" />
                {claim && <ImageUpload secure="finder" value={form.photo_2} onChange={(v) => setForm((f) => ({ ...f, photo_2: v }))} testid="fd-photo2" />}
              </div>
            </div>
            {claim && <div><Label>{t("an.message")}</Label><Textarea value={form.message} onChange={set("message")} className="mt-1.5 rounded-xl" data-testid="fd-message" /></div>}
            <label className="flex items-start gap-2.5 rounded-xl bg-slate-50 border border-border p-3 cursor-pointer" data-testid="fd-consent-label">
              <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5 h-4 w-4 accent-findo-navy" data-testid="fd-consent" />
              <span className="text-xs text-slate-600 leading-relaxed">{t("an.shareConsent")}</span>
            </label>
            <Button type="submit" disabled={busy || !form.consent || !photosOk} className="w-full h-12 rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="fd-submit">
              {busy ? <FindoSpinner size={16} /> : claim ? t("fa.sendClaim") : t("fa.submitDecl")}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
